import React from "react"
import { Link } from "gatsby"
import styled from "styled-components/macro"
import colors from "../scripts/colors.ts"
import PageLayout from "../components/PageLayout"
import PageTitle from "../components/PageTitle/PageTitle"
import { PageMargin } from "../GlobalStyles"
import ArticleContent from "../components/Article/ArticleContent"
import ArticleShare from "../components/Article/ArticleShare"
import MoreInfo from "../components/MoreInfo/MoreInfo"
import MoreInfoContact from "../components/MoreInfo/MoreInfoContact"
import "@fontsource/raleway/300.css"
import "@fontsource/noto-sans-jp/900.css"
import { Helmet } from "react-helmet"
import useFacilityData from "../hooks/useFacilityData.ts"
import { AdvancedImage, responsive } from "@cloudinary/react"
import { Cloudinary } from "@cloudinary/base"
import { fill } from "@cloudinary/base/actions/resize"
import {
  compass,
  focusOn,
  autoGravity,
} from "@cloudinary/base/qualifiers/gravity"
import { faces } from "@cloudinary/base/qualifiers/focusOn"
import { format, quality } from "@cloudinary/base/actions/delivery"
import { auto as fAuto } from "@cloudinary/base/qualifiers/format"
import { auto as qAuto } from "@cloudinary/base/qualifiers/quality"
import { north } from "@cloudinary/base/qualifiers/compass"
import { decode } from "js-base64"
import jsonLdArticle from "../scripts/jsonLdArticle.ts"
import { paramCase } from "param-case"

const bannerImgUrl = (imgId, width) =>
  `https://res.cloudinary.com/agoraservices/w_475,q_auto,f_auto/${imgId}`

const ArticleMargin = styled(PageMargin)`
  #Article-socialShare--sideBar {
    display: none;
    @media screen and (min-width: 1024px) {
      display: flex;
    }
  }
  #Article-socialShare--footer {
    display: flex;
    /* position: fixed;
    bottom: 20px; */
    align-self: center;
    justify-content: center;
    margin-top: 50px;
    .MoreInfo-styles {
      align-items: center;
    }
    @media screen and (min-width: 1024px) {
      display: none;
    }
  }
`

const Date = styled.p`
  font-family: "Noto Sans JP", sans-serif;
  font-size: 15px;
  text-transform: uppercase;
  color: ${colors("grey", "dk")};
  text-align: center;
  margin: 0;
`

const TitleWrap = styled.header`
  max-width: 750px;
  margin: 0 auto;
  h1 {
    text-align: center;
    line-height: 1.3em;
  }
`

const ContentWrap = styled.div`
  display: flex;
  justify-content: stretch;
  flex-direction: column;
  font-size: 20px;
  line-height: 30px;
  font-weight: 300;
  margin-bottom: 100px;
  p {
    margin: 0 0 1rem;
  }
  @media screen and (min-width: 1024px) {
    flex-direction: row;
  }
`

const SidebarWrap = styled.aside`
  flex: 0 0 auto;
  width: 100%;
  margin-right: 0;
  position: relative;
  @media screen and (min-width: 1024px) {
    width: 475px;
    margin-right: 37px;
  }
`

const Sidebar = styled.div`
  position: sticky;
  top: 10px;
  display: flex;
  flex-direction: column;
  .MoreInfo-styles {
    .MoreInfo-content {
      flex-direction: row;
    }
  }
`

const Banner = styled.section`
  /* width: 100%; */
  height: auto;
  border: 1px solid ${colors("grey", "dk")};
  margin-bottom: 50px;
  img {
    display: block;
    width: 100%;
  }
  @media screen and (min-width: 1024px) {
    /* height: 475px; */
    margin-bottom: 0;
  }
`

const Article = ({ location, pageContext }) => {
  const facilityData = useFacilityData()

  const facilityInfo = facilityData.sanityFacility.facilityObj
  const metaData = facilityData.site.siteMetadata

  // BANNER IMAGE
  const imgId = JSON.parse(
    decode(pageContext.bannerImage.asset.source.id)
  ).public_id

  const shareUrl = String(location.href)

  const title = pageContext.metaTitle
    ? pageContext.metaTitle
    : `${pageContext.title} < News < ${facilityInfo.facilityName}`

  const url = `${metaData.siteUrl}/news/${pageContext.slug}`

  return (
    <PageLayout currLocation={`/news/${pageContext.slug}`}>
      <Helmet>
        <title>{title}</title>
        <meta name="title" content={title} />
        <meta name="description" content={pageContext.summary} />
        <link
          rel="canonical"
          href={pageContext.canonicalUrl ? pageContext.canonicalUrl : url}
        />

        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={pageContext.canonicalUrl ? pageContext.canonicalUrl : url}
        />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={pageContext.summary} />
        <meta
          property="og:image"
          itemProp="image"
          content={`https://res.cloudinary.com/agoraservices/q_auto,f_auto/${imgId}`}
        />

        <meta property="twitter:card" content="summary_large_image" />
        <meta
          property="twitter:url"
          content={pageContext.canonicalUrl ? pageContext.canonicalUrl : url}
        />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={pageContext.summary} />
        <meta
          property="twitter:image"
          content={`https://res.cloudinary.com/agoraservices/q_auto,f_auto/${imgId}`}
        />

        <script type="application/ld+json">
          {JSON.stringify(jsonLdArticle(pageContext, metaData, facilityInfo))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                item: {
                  "@id": `${metaData.siteUrl}/news`,
                  name: "News",
                },
              },
              {
                "@type": "ListItem",
                position: 2,
                item: {
                  "@id": `${metaData.siteUrl}/news/${paramCase(
                    pageContext.title
                  )}`,
                  name: pageContext.title,
                },
              },
            ],
          })}
        </script>
      </Helmet>
      <article role="article">
        <ArticleMargin>
          <TitleWrap>
            <Date>{pageContext.date}</Date>
            <PageTitle>{pageContext.title}</PageTitle>
          </TitleWrap>
          <ContentWrap>
            <SidebarWrap role="complementary">
              <Sidebar>
                <Banner role="region">
                  <img
                    loading="lazy"
                    src={bannerImgUrl(imgId)}
                    alt={pageContext.title}
                  />
                </Banner>
                <ArticleShare
                  id="Article-socialShare--sideBar"
                  shareUrl={shareUrl}
                />
              </Sidebar>
            </SidebarWrap>
            <ArticleContent content={pageContext._rawContent} />
          </ContentWrap>
          <footer>
            <MoreInfoContact />
            <ArticleShare
              id="Article-socialShare--footer"
              shareUrl={shareUrl}
            />
          </footer>
        </ArticleMargin>
      </article>
    </PageLayout>
  )
}

export default Article
